import { shuffle, type CardInstance } from "@reagent/sim";
import { ALL_CARDS, CARDS } from "@reagent/content";
import type { RunState, ShopOffer, UpgradeKind } from "./types";
import { canBuyUpgrade, UPGRADES, upgradeDef } from "./upgrades";
import { currentStage, recordChoice, shopRng } from "./run";

/**
 * The between-stages shop.
 *
 * Offers are generated from the run's seed, so the same run always meets the
 * same supplier — a run is reproducible from seed plus choices, and choices
 * only mean something if what was on offer was fixed.
 */

/** Cards cost roughly ten credits per kW, so power is the real currency. */
const CARD_COST_PER_KW = 11;
const CARD_BASE_COST = 14;
const CARDS_OFFERED = 3;

/** Trimming gets pricier as the deck gets tighter — the last cut is the best. */
const TRIM_BASE_COST = 25;
const TRIM_STEP = 15;

export function cardCost(cardId: string): number {
  const card = CARDS[cardId];
  return CARD_BASE_COST + (card?.costKw ?? 1) * CARD_COST_PER_KW;
}

export function trimCost(run: RunState): number {
  const trims = run.choices.filter((c) => c.type === "trim").length;
  return TRIM_BASE_COST + trims * TRIM_STEP;
}

/**
 * What this shop is selling. Hazard cards are never on offer — they arrive by
 * neglect (§3.2), and selling them back would make the deck-pollution
 * mechanic meaningless.
 */
export function offersFor(run: RunState): readonly ShopOffer[] {
  const rng = shopRng(run);
  // Spec 0068 (owner, 2026-10-02): a recipe-bound machine card is sold only by its module's setupCatalogue, never here.
  const buyable = ALL_CARDS.filter((c) => c.category !== "hazard" && !c.deploysNodeConfig?.recipeIds).map((c) => c.id);

  const cards = shuffle(buyable, rng)
    .slice(0, CARDS_OFFERED)
    .map((cardId): ShopOffer => ({ kind: "card", cardId, cost: cardCost(cardId) }));

  const upgrades = UPGRADES.filter((u) => canBuyUpgrade(run, u.kind)).map(
    (upgrade): ShopOffer => ({ kind: "upgrade", upgrade }),
  );

  return [...cards, ...upgrades, { kind: "trim", cost: trimCost(run) }];
}

export function canAfford(run: RunState, cost: number): boolean {
  return run.credits >= cost;
}

/** Buys a card into the deck. Rejected purchases leave the run untouched. */
export function draftCard(run: RunState, cardId: string): RunState {
  const cost = cardCost(cardId);
  if (!canAfford(run, cost)) return run;
  if (!CARDS[cardId]) return run;

  const instance: CardInstance = {
    instanceId: `r${run.nextInstanceSeq}`,
    cardId,
    acquiredStageIndex: run.stageIndex,
    boundNodeId: null,
    isContaminated: false,
  };

  return recordChoice(
    {
      ...run,
      credits: run.credits - cost,
      deck: [...run.deck, instance],
      nextInstanceSeq: run.nextInstanceSeq + 1,
    },
    { type: "draft", cardId },
  );
}

/**
 * The smallest a deck may be trimmed to: the largest hand size this run's
 * upgrades can produce, so a trimmed deck can always deal a full hand.
 * `currentStage` already runs `composeStage`'s `biggerHand` math once,
 * correctly — this reuses it rather than duplicating it. The literal 5 is
 * only a defensive fallback for a stage index the shop can't actually reach.
 */
function minTrimSize(run: RunState): number {
  return currentStage(run)?.handSize ?? 5;
}

/**
 * Removes one copy of a card from the deck.
 *
 * Trimming is how a deck-builder actually improves: the cards you *don't* draw
 * are as decisive as the ones you do, and this is the only way to get rid of
 * the hazards neglect put there.
 */
export function trimCard(run: RunState, cardId: string): RunState {
  const cost = trimCost(run);
  if (!canAfford(run, cost)) return run;

  const index = run.deck.findIndex((instance) => instance.cardId === cardId);
  if (index === -1) return run;
  // A deck that can be trimmed below a dealable hand is a deck that can't
  // deal a hand.
  if (run.deck.length <= minTrimSize(run)) return run;

  const deck = [...run.deck];
  deck.splice(index, 1);

  return recordChoice({ ...run, credits: run.credits - cost, deck }, { type: "trim", cardId });
}

export function buyUpgrade(run: RunState, kind: UpgradeKind): RunState {
  const def = upgradeDef(kind);
  if (!canAfford(run, def.cost)) return run;
  if (!canBuyUpgrade(run, kind)) return run;

  return recordChoice(
    { ...run, credits: run.credits - def.cost, upgrades: [...run.upgrades, kind] },
    { type: "upgrade", kind },
  );
}
